import { IconChevronLeft } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { AstraButton, AstraIcon, AstraParagraph } from '~/astra'
import { PractitionerChipWithTooltip } from '~/components'
import { activateRecurringAssignment, rejectRecurringAssignment } from '~/store/di-entity.api'
import { DEFAULT_RECURRING_ASSIGNMENT_INCLUDE, useRecurringAssignment } from '~/storm'

import { EditRecurringAssignment } from '../Edit/EditRecurringAssignment'
import { RecurringAssignmentPreview } from '../RecurringAssignmentPreview'
import { ChatMessage, LLMResponse } from './types'

type Props = {
    llmResponse: LLMResponse
    addMessage: (message: ChatMessage) => void
    onBackClick: () => void
}

const tadaEmoji = String.fromCodePoint(0x1f389)

export const LLMPreviewMessage = ({ llmResponse, addMessage, onBackClick }: Props) => {
    const [isEditing, setIsEditing] = useState(false)
    const assignmentUuid = llmResponse.type === 'error' ? undefined : llmResponse.recurring_assignment_uuid
    const assignment = useRecurringAssignment({ where: { uuid: assignmentUuid }, include: DEFAULT_RECURRING_ASSIGNMENT_INCLUDE })

    const addSuccessMessage = () => {
        addMessage({
            type: 'LLMText',
            message: (
                <span className="flex flex-col gap-1.5">
                    <AstraParagraph variant="body" weight="strong">
                        Hurra {tadaEmoji} regel lagret!
                    </AstraParagraph>
                    <AstraParagraph className="text-muted">
                        Gå
                        <span className="inline-block px-1.5 align-baseline">
                            <AstraButton size="small" variant="secondary" icon={<AstraIcon icon={IconChevronLeft} />} label="tilbake" onClick={onBackClick} />
                        </span>
                        for å se alle regler, eller fortsett å lage ny regel nedenfor.
                    </AstraParagraph>
                </span>
            ),
        })
    }
    const activateAssignmentMutation = useMutation({
        mutationFn: activateRecurringAssignment,
        onSuccess: () => {
            addSuccessMessage()
        },
    })

    const rejectAssignmentMutation = useMutation({
        mutationFn: rejectRecurringAssignment,
        onSuccess: () => {
            addMessage({
                type: 'LLMText',
                message: 'Regel ble avslått, prøv igjen med å presisere aktivitet, dag og partall eller oddetall hvis det er relevant.',
            })
        },
    })

    const anyPending = activateAssignmentMutation.isPending || rejectAssignmentMutation.isPending
    const error = activateAssignmentMutation.error || rejectAssignmentMutation.error

    if (llmResponse.type === 'error') {
        // this is error formatted from the LLM
        return <AstraParagraph>{llmResponse.message}</AstraParagraph>
    }

    if (!assignment) {
        // this could mean loading or something wrong (ie assignmment not found)
        return null
    }

    return (
        <div className="flex flex-col gap-2">
            {/* Cannot use AstraParagraph due to nested p-tags with PractitionerChip */}
            <div className="text-body leading-body tracking-body text-muted">
                Lager repeterende regel for{' '}
                <span className="inline-block align-baseline">
                    {assignment.practitioner ? <PractitionerChipWithTooltip shortName={assignment.practitioner.short_name} /> : 'kirurg'}
                </span>
                . Ser dette riktig ut?
            </div>
            <div className="rounded-lg outline outline-1 outline-offset-[-1px] outline-base">
                {isEditing ? (
                    <div className="">
                        <EditRecurringAssignment assignment={assignment} onClose={() => setIsEditing(false)} outerPadding />
                    </div>
                ) : (
                    <RecurringAssignmentPreview assignment={assignment} />
                )}
                {error && <AstraParagraph className="px-4 py-2 text-critical">Det oppstod en feil. Vennligst prøv igjen.</AstraParagraph>}
                {!isEditing && (
                    <div className="flex justify-end gap-2 px-4 py-2">
                        <AstraButton
                            size="small"
                            variant="ghost"
                            label="Endre"
                            disabled={anyPending || assignment.entity_status === 'rejected'}
                            onClick={() => {
                                setIsEditing(edit => !edit)
                            }}
                        />
                        <AstraButton
                            size="small"
                            variant="critical-secondary"
                            label="Avslå"
                            disabled={anyPending || assignment.entity_status === 'rejected'}
                            onClick={() => {
                                rejectAssignmentMutation.mutate(assignment.uuid)
                            }}
                        />
                        <AstraButton
                            size="small"
                            variant="primary"
                            label="Lagre regel"
                            disabled={anyPending || assignment.entity_status === 'active'}
                            onClick={() => {
                                activateAssignmentMutation.mutate(assignment.uuid)
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
